import {useEffect, useRef, useState} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {
    MeasureRawWrapper,
    MeasureRawTopbar,
    MeasureRawTopbarTitle,
    MeasureRawContent,
    MeasureRawIframe,
    StyledIconButton
} from './style'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {Tooltip} from '@components/ui/tooltip/Tooltip.tsx'
import {LinkIcon, PrintIcon, DownloadIcon} from '@/components/ui/icon/Icon'
import {Spinner} from '@components/ui/spinner/Spinner.tsx'
import {useGetMeasureRawHtml} from '@/features/measure-details/services/useGetMeasureRawHtml.ts'
import {useGetMeasureDetails} from '@/features/measure-details/services/useGetMeasureDetails.ts'
import {copyToClipboard} from '@/utilities/helpers'
import {toast} from 'react-hot-toast'
import {routes} from '@utilities/constants/routes.tsx'
import {Button} from '@components/ui/button/Button.tsx'
import {
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    ModalTitle
} from '@components/ui/modal-atoms/ModalAtoms.tsx'
import {envVars} from '@/envVars'

export const MeasureRaw = () => {
    const {t} = useTranslation()
    const params = useParams()
    const navigate = useNavigate()
    const measureQuery = useGetMeasureRawHtml()
    const detailsQuery = useGetMeasureDetails(params?.id)
    const iframeRef = useRef<HTMLIFrameElement>(null)
    const [showWarning, setShowWarning] = useState(false)
    const [showContent, setShowContent] = useState(false)

    useEffect(() => {
        if (params?.id && detailsQuery.isSuccess) {
            // Check if the measure is omissis
            if (detailsQuery.data?.isOmissis && !showContent) {
                setShowWarning(true)
            } else {
                // If not omissis or user has confirmed, load the raw HTML
                measureQuery.mutate(params.id)
            }
        }
    }, [params?.id, detailsQuery.isSuccess, detailsQuery.data?.isOmissis, showContent])

    const handleCopyLink = () => {
        const link = `${window.location.origin}/${params?.id}`
        copyToClipboard(link)
        toast.success(t('commons:copy_success'))
    }

    const handlePrint = () => {
        if (iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.print()
        }
    }

    useEffect(() => {
        const handleKeyboardShortcut = (e: KeyboardEvent) => {
            // Check for Cmd+P (Mac) or Ctrl+P (Windows/Linux)
            if ((e.metaKey || e.ctrlKey) && e.key === 'p') {
                e.preventDefault()
                handlePrint()
            }
        }

        document.addEventListener('keydown', handleKeyboardShortcut)

        return () => {
            document.removeEventListener('keydown', handleKeyboardShortcut)
        }
    }, [handlePrint])

    const handleDownload = () => {
        if (measureQuery.data?.data) {
            const blob = new Blob([measureQuery.data.data], {type: 'text/html'})
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `provvedimento-${params?.id}.html`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
        }
    }

    const handleConfirmView = () => {
        setShowWarning(false)
        setShowContent(true)
        if (params?.id) {
            measureQuery.mutate(params.id)
        }
    }

    const handleCancelView = () => {
        setShowWarning(false)
        navigate(routes.MEASURES.path)
    }

    const isLoading = detailsQuery.isLoading || measureQuery.isPending
    const isError = detailsQuery.isError || measureQuery.isError

    return (
        <>
            {showWarning && (
                <>
                    <ModalOverlay />
                    <Modal width="400px">
                        <ModalHeader>
                            <ModalTitle>{t('measures:omissis_warning:title')}</ModalTitle>
                        </ModalHeader>
                        <ModalBody>
                            <p style={{whiteSpace: 'pre-line'}}>{t('measures:omissis_warning:message')}</p>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" onClick={handleCancelView}>
                                {t('commons:cancel')}
                            </Button>
                            <Button onClick={handleConfirmView}>{t('measures:omissis_warning:proceed')}</Button>
                        </ModalFooter>
                    </Modal>
                </>
            )}
            <MeasureRawWrapper>
                {!envVars.VITE_HIDE_MEASURE_DETAIL_TOPBAR && (
                    <MeasureRawTopbar>
                        <MeasureRawTopbarTitle onClick={() => navigate(routes.MEASURES.path)}>
                            {t('commons:title')}
                        </MeasureRawTopbarTitle>

                        <Flexbox gap={2} align={'center'}>
                            <Tooltip content={t('measure_details:copy_link')} isOpenOnClickEnabled={false}>
                                <StyledIconButton variant={'primaryLight'} onClick={handleCopyLink}>
                                    <LinkIcon fill="none" size={20} />
                                </StyledIconButton>
                            </Tooltip>

                            <Tooltip content={t('measure_details:print_raw')} isOpenOnClickEnabled={false}>
                                <StyledIconButton variant={'primaryLight'} onClick={handlePrint}>
                                    <PrintIcon fill="none" size={20} />
                                </StyledIconButton>
                            </Tooltip>

                            <Tooltip content={t('measure_details:download')} isOpenOnClickEnabled={false}>
                                <StyledIconButton variant={'primaryLight'} onClick={handleDownload}>
                                    <DownloadIcon fill="none" size={20} />
                                </StyledIconButton>
                            </Tooltip>
                        </Flexbox>
                    </MeasureRawTopbar>
                )}

                <MeasureRawContent>
                    {isLoading && (
                        <Flexbox justify={'center'} align={'center'} style={{height: '100%'}}>
                            <Spinner size={40} />
                        </Flexbox>
                    )}
                    {isError && (
                        <Flexbox justify={'center'} align={'center'} style={{height: '100%'}}>
                            <p>{t('errors:default')}</p>
                        </Flexbox>
                    )}
                    {measureQuery.isSuccess && measureQuery.data?.data && (
                        <MeasureRawIframe ref={iframeRef} srcDoc={measureQuery.data.data} title="Provvedimento" />
                    )}
                </MeasureRawContent>
            </MeasureRawWrapper>
        </>
    )
}
